import { formatInTimeZone } from 'date-fns-tz';

import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  isValid,
  parseISO,
} from 'date-fns';
import { ko } from 'date-fns/locale/ko';
import { isNil } from 'lodash-es';

export function getRelativeTime(utcTimeString: string): string {
  const date = parseISO(utcTimeString);
  const now = new Date();

  const secondsDiff = differenceInSeconds(now, date);
  const minutesDiff = differenceInMinutes(now, date);
  const hoursDiff = differenceInHours(now, date);
  const daysDiff = differenceInDays(now, date);

  // 7일 이상: YYYY.MM.DD HH:MM 형식
  if (daysDiff >= 7) {
    return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd HH:mm');
  }

  // 7일 미만: 상대 시간 표시
  if (daysDiff > 0) return `${daysDiff}일 전`;
  if (hoursDiff > 0) return `${hoursDiff}시간 전`;
  if (minutesDiff > 0) return `${minutesDiff}분 전`;
  if (secondsDiff > 10) return `${secondsDiff}초 전`;

  return '방금 전';
}

export const formatFullDate = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd(EEE) HH:mm:ss', {
    locale: ko,
  });
};

export const formatFullDateExceptSeconds1 = (
  date: string | number | Date
): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd(EEE) HH:mm', {
    locale: ko,
  });
};

export const formatFullDateExceptSeconds2 = (
  date: string | number | Date
): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy/MM/dd(EEE) HH:mm', {
    locale: ko,
  });
};

export const formatDateTime = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy-MM-dd HH:mm:ss');
};

export const formatDayTimeDate = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd HH:mm');
};

export const formatDayTimeDateA = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd HH:mm a');
};

export const formatDayDate = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd');
};

export const formatInputDateTime = (date: string | number | Date): string => {
  if (date === '' || date === null || date === undefined) return '';
  try {
    const dateObj = new Date(date);
    if (!isValid(dateObj)) return '';
    return formatInTimeZone(dateObj, 'Asia/Seoul', "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

export const formatRangeDate = (date?: string | number | Date): string => {
  if (isNil(date)) return '';
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd HH:mm');
};

export const formatDateTimeDot = (date: string | number | Date): string =>
  formatInTimeZone(date, 'Asia/Seoul', 'yyyy.MM.dd HH:mm:ss');

export const formatShortDayOfWeek = (date: string | number | Date): string =>
  formatInTimeZone(date, 'Asia/Seoul', 'yy. MM. dd (EEE)', { locale: ko });

export const formatInputDate1 = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy-MM-dd');
};

export const formatInputDate2 = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'yyyy. MM. dd');
};

export const formatMonthDate = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'MM/dd HH:mm');
};

export const formatMonthDateWithDot = (
  date: string | number | Date
): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'MM.dd HH:mm');
};

export const formatHourMinutesDate = (date: string | number | Date): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'HH:mm');
};

export const formatHourMinutesSecondsDate = (
  date: string | number | Date
): string => {
  return formatInTimeZone(date, 'Asia/Seoul', 'HH:mm:ss');
};

export const isValidDate = (value?: string | number | Date): value is Date => {
  return isValid(value);
};

export const getDaysFromNow = (targetDate: string): number => {
  return differenceInDays(parseISO(targetDate), new Date());
};
